import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import Select, { SelectProps } from '@material-ui/core/Select';
import { ClassApi } from '../../types/styles';
import { useId } from '../../hooks';
import { GenericItem } from '../../types/items';
import { CustomActionCell } from '../index';
import { QuestionCircleIcon } from '../icon/solid';

const useStyles = makeStyles(({ palette, spacing, colors }: Theme) => createStyles({
  formControl: {
    minWidth: 120,
  },
  root: {
    marginTop: spacing(2),
    '&:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&&:hover:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$focused:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$disabled:before': {
      borderBottomStyle: 'dashed',
    },
  },
  label: {
    '&$focused': {
      color: palette.text.secondary,
    },
  },
  focused: {},
  disabled: {},

  iconTooltip: {
    fontSize: 14,
    color: colors.icon,
    verticalAlign: 'middle',
    marginBottom: 4,
    marginLeft: 8,
  },
  commentButtonWrapper: {
    textAlign: 'center',
  },
  comment: {
    fontSize: '12px',
    padding: '0 10px 0 10px',
  },
}), { name: 'FormSelect' });

type Props = Omit<SelectProps, 'classes'> & {
  label?: string;
  name: string;
  hideErrors?: boolean;
  isNone?: boolean;
  classes?: ClassApi<typeof useStyles>;
  items: Array<GenericItem<number | string>> | null;
  disabled?: boolean;
  tooltip?: string;
};

const FormSelect = ({
  name,
  label,
  disabled = false,
  hideErrors = false,
  className,
  items,
  isNone = false,
  tooltip = '',
}: Props) => {
  const classes = useStyles();

  const { errors, formState, control, watch } = useFormContext();
  const value = watch(name);
  const selectId = useId('form-select-');
  const errorId = useId('input-error-');

  return (
    <FormControl
      fullWidth
      disabled={disabled || !(items && items.length)}
      className={classes.formControl}
      error={Boolean(formState.touched[name] && errors[name])}
    >
      {label && (
        <InputLabel
          htmlFor={selectId}
          className={classes.label}
          classes={{
            root: classes.label,
            focused: classes.focused,
          }}
        >
          <>
            {label}
            {tooltip && (
              <CustomActionCell
                title={<QuestionCircleIcon className={`${classes.iconTooltip}`} />}
                classNameButtonWrapper={classes.commentButtonWrapper}
                onMouse
              >
                <p className={classes.comment}>
                  {tooltip}
                </p>
              </CustomActionCell>
            )}
          </>
        </InputLabel>
      )}
      <Controller
        name={name}
        control={control}
        as={(
          <Select
            id={selectId}
            className={classes.root}
            disabled={disabled || !(items && items.length)}
            classes={{
              disabled: classes.disabled,
            }}
            MenuProps={{
              style: {
                zIndex: 2147483100,
              },
            }}
          >
            {isNone
            && (
              <MenuItem value={value || ''}>
                <em>None</em>
              </MenuItem>
            )}
            {items
            && items.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {item.title}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      {formState.touched[name] && errors[name] && !hideErrors && (
        <FormHelperText id={errorId}>{errors[name]?.message}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormSelect;
